import React, { useEffect, useRef } from 'react';
import { Icon } from 'lucide-react';
import { soccerBall } from '@lucide/lab';
import { hapticSuccess } from '@/services/haptics';

interface GoalCelebrationProps {
  onComplete: () => void;
  message?: string;
}

const GoalCelebration: React.FC<GoalCelebrationProps> = ({
  onComplete,
  message = 'LINEUP LOCKED IN!',
}) => {
  // Buzz once and dismiss once per celebration, regardless of onComplete's
  // identity — callers pass inline arrows, and re-running this effect on every
  // parent re-render would re-fire the haptic and keep resetting the timer.
  const onCompleteRef = useRef(onComplete);
  const reducedMotionRef = useRef(
    typeof window !== 'undefined'
      && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
  );
  const reducedMotion = reducedMotionRef.current;
  onCompleteRef.current = onComplete;
  useEffect(() => {
    hapticSuccess();
    const timer = setTimeout(
      () => onCompleteRef.current(),
      reducedMotionRef.current ? 1200 : 2200,
    );
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className={`fixed inset-0 z-[200] flex flex-col items-center justify-center ${reducedMotion ? '' : 'animate-fade-in'}`}>
      {/* Nested backdrop — kept separate from the root so a parent
          `[&>*]:!bg-transparent` rule (see App.tsx content wrapper) can't strip it. */}
      <div className="absolute inset-0 bg-primary/90 pointer-events-none" />
      <style>{`
        @keyframes ballArc {
          0% {
            transform: translateY(60vh) translateX(-20px) rotate(0deg) scale(0.6);
            opacity: 0;
          }
          15% {
            opacity: 1;
          }
          60% {
            transform: translateY(0) translateX(10px) rotate(360deg) scale(1);
          }
          75% {
            transform: translateY(0) translateX(4px) rotate(360deg) scale(1.35);
          }
          100% {
            transform: translateY(0) translateX(0) rotate(360deg) scale(1);
          }
        }
        @keyframes burstGlow {
          0%, 55% {
            opacity: 0;
            transform: scale(0.5);
          }
          70% {
            opacity: 1;
            transform: scale(1.2);
          }
          100% {
            opacity: 0;
            transform: scale(2);
          }
        }
        @keyframes textReveal {
          0%, 65% {
            opacity: 0;
            transform: translateY(12px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      {/* Burst glow behind the ball */}
      {!reducedMotion && (
        <div
          className="absolute rounded-full bg-accent-yellow/30 blur-2xl"
          style={{
            width: 160,
            height: 160,
            animation: 'burstGlow 1.8s ease-out forwards',
          }}
        />
      )}

      {/* Soccer ball */}
      <div
        className="text-accent-yellow"
        style={reducedMotion ? undefined : {
          animation: 'ballArc 1.2s cubic-bezier(0.22, 1, 0.36, 1) forwards',
        }}
      >
        <Icon iconNode={soccerBall} size={72} strokeWidth={1.5} />
      </div>

      {/* Text */}
      <p
        className="mt-6 font-heading font-bold text-2xl md:text-3xl text-accent-yellow tracking-wider"
        style={reducedMotion ? undefined : {
          animation: 'textReveal 1.8s ease-out forwards',
        }}
      >
        {message}
      </p>
    </div>
  );
};

export default GoalCelebration;
